import {
  Checkbox,
  Dropdown,
  InlineNotification,
  Modal,
  TimePicker,
} from '@carbon/react';
import { DatePicker } from 'components/date-picker';
import {
  ExportImageOptionsFields,
  ALL_ASPECT_RATIO_OPTIONS,
  GENERIC_CHART_ELEMENTS,
  type ExportImageOptions,
  type OutputFormat,
} from 'components/chart-export';
import {
  COLUMN_PRESETS,
  COLUMN_PRESET_SECTIONS,
  type ContentExportItem,
  type ContentExportItemId,
  type ColumnPresetOption,
  type ColumnPresetSection,
} from './utils';
import {
  SpatialLayerPicker,
  canExport,
  SPATIAL_EXPORT_VALIDATION_HINT,
} from 'components/export-spatial-data';
import { SPATIAL_EXPORT_LAYERS } from './spatial-export-utils';
import type { ColumnPreset, SpatialExportConfig } from './types';
import styles from './export-content-modal.module.scss';

/** Formats offered for the exported images (the modal's Image Options block). */
const IMAGE_EXPORT_FORMATS: OutputFormat[] = ['png', 'jpeg'];

/**
 * Extracts the time portion (HH:MM) from an ISO-ish datetime string, or returns the current time.
 *
 * @param {string | undefined} datetime - An ISO datetime string.
 * @return {string} The time as HH:MM (24h).
 */
function extractTime(datetime: string | undefined): string {
  if (!datetime || datetime.length <= 10) {
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  }
  return datetime.slice(11, 16);
}

/**
 * Extracts the date portion (YYYY-MM-DD) from an ISO-ish datetime string.
 *
 * @param {string | undefined} datetime - An ISO datetime string.
 * @return {string | undefined} The date portion.
 */
function extractDate(datetime: string | undefined): string | undefined {
  return datetime?.slice(0, 10);
}

export interface ExportContentModalProps {
  /** Whether the modal is open. */
  open: boolean;
  /** The checklist items to render (in display order), from `CONTENT_EXPORT_ITEMS`. */
  items: ContentExportItem[];
  /** The currently-checked item ids. */
  checkedIds: ReadonlySet<ContentExportItemId>;
  /** Toggle a checklist item on/off. */
  onToggleItem: (id: ContentExportItemId, checked: boolean) => void;
  /** Items whose checkbox is disabled (e.g. Charts & Visuals when the section isn't rendered). */
  disabledIds?: ReadonlySet<ContentExportItemId>;
  /** True when exporting at the package/workspace level (no evaluation). Hides eval-only UI. */
  isPackageMode?: boolean;
  /** ISO datetime of the evaluation's run (shown read-only when basis is eval_run). */
  evalRunDatetime?: string | null;
  /** When true, render the Image Options block (an image item is checked). */
  showImageOptions: boolean;
  /** The shared Image Options value (controlled). */
  imageOptions: ExportImageOptions;
  /** Called with the next Image Options on any field change. */
  onImageOptionsChange: (next: ExportImageOptions) => void;
  /** Spatial data config (controlled). */
  spatialConfig: SpatialExportConfig;
  /** Called with the next spatial config on any field change. */
  onSpatialConfigChange: (next: SpatialExportConfig) => void;
  /**
   * Per-table column preset for Properties / Property Allocations / Cashflows by Month
   * (controlled) + its setter. The dropdown beside each of those checkboxes is live only while
   * the box is checked.
   */
  columnPresets: {
    value: Record<ColumnPresetSection, ColumnPreset>;
    onChange: (section: ColumnPresetSection, preset: ColumnPreset) => void;
  };
  /** True while capturing + uploading — disables the footer + shows the in-flight label. */
  isExporting: boolean;
  /** A user-facing error (POST failure or a failed completion), shown inline. */
  error?: string | null;
  /** Submit the export (Export button / Enter). */
  onExport: () => void;
  /** Dismiss the modal (Cancel / Esc / backdrop). */
  onClose: () => void;
}

/**
 * The Export Content modal (P8 · Slice 1) — a presentational Carbon `md` modal whose
 * checklist picks which output of the evaluation to bundle into the export ZIP, plus an
 * embedded Image Options form (shown when an image item is checked) governing how the
 * captured chart PNGs render. Purely prop-driven: the connected owner (`ContentExport`) owns
 * the checked set, the disabled gate, the Image Options value, and the async submit — so this
 * component is trivially testable and free of page-state coupling.
 *
 * Export is enabled once ≥1 item is checked (the mock gate; the BE's ≥1-data-section rule is a
 * deferred reconciliation). One item — Charts & Visuals — can be disabled (WYSIWYG: nothing to
 * capture when the section isn't rendered); the owner passes it in `disabledIds`.
 *
 * @param {ExportContentModalProps} props - The checklist, checked/disabled sets, Image Options, and the submit/close actions.
 * @return {JSX.Element} The modal.
 */
export function ExportContentModal({
  open,
  items,
  checkedIds,
  onToggleItem,
  disabledIds,
  showImageOptions,
  imageOptions,
  onImageOptionsChange,
  spatialConfig,
  onSpatialConfigChange,
  isPackageMode = false,
  columnPresets,
  isExporting,
  error,
  onExport,
  onClose,
}: ExportContentModalProps) {
  // When Spatial Data is checked, the export must name at least one layer and one format — otherwise
  // the BE's `Array(...).presence` fallback silently exports every layer/format, the opposite of an
  // empty selection. The same `canExport` rule the shared ExportSpatialData dialog uses (now widened
  // to `readonly string[]`, so this modal's `string[]` config reuses it rather than re-encoding it).
  const spatialExportInvalid =
    checkedIds.has('spatial_data') &&
    !canExport(spatialConfig.layers, spatialConfig.formats);
  return (
    <Modal
      open={open}
      size="md"
      modalHeading={
        isPackageMode ? 'Export content' : 'Export evaluation content'
      }
      primaryButtonText={isExporting ? 'Exporting…' : 'Export'}
      secondaryButtonText="Cancel"
      // Mock gate (FR5): enabled once ≥1 item is checked, not mid-export, and — when Spatial Data is
      // checked — only with at least one layer and one format selected.
      primaryButtonDisabled={
        checkedIds.size === 0 || isExporting || spatialExportInvalid
      }
      onRequestSubmit={onExport}
      onRequestClose={onClose}
    >
      <p className={styles.intro}>
        {isPackageMode
          ? 'Pick which sections to include in the export.'
          : 'Pick which sections of this evaluation to include in the export.'}
      </p>

      {/* Labelled group with no visible legend (UI/UX req) — the checklist items. */}
      <div
        role="group"
        aria-label="Content to export"
        className={styles.checklist}
      >
        {(() => {
          const enabledItems = items.filter(
            (item) => !disabledIds?.has(item.id)
          );
          const allChecked =
            enabledItems.length > 0 &&
            enabledItems.every((item) => checkedIds.has(item.id));
          const someChecked = enabledItems.some((item) =>
            checkedIds.has(item.id)
          );
          return (
            <div className={styles.checklistRow}>
              <Checkbox
                id="content-export-select-all"
                labelText="Select All"
                checked={allChecked}
                indeterminate={someChecked && !allChecked}
                onChange={(_event, { checked }) => {
                  enabledItems.forEach((item) =>
                    onToggleItem(item.id, checked)
                  );
                }}
              />
            </div>
          );
        })()}
        <div className={styles.checklistChildren}>
          {items.map((item) => {
            // Properties / Property Allocations / Cashflows by Month carry a column-preset
            // dropdown; every other item is a plain checkbox. `find` narrows item.id to a
            // ColumnPresetSection.
            const presetSection = COLUMN_PRESET_SECTIONS.find(
              (section) => section === item.id
            );
            return (
              <div key={item.id} className={styles.checklistRow}>
                <Checkbox
                  id={`content-export-${item.id}`}
                  labelText={item.label}
                  checked={checkedIds.has(item.id)}
                  disabled={disabledIds?.has(item.id) ?? false}
                  onChange={(_event, { checked }) =>
                    onToggleItem(item.id, checked)
                  }
                />
                {presetSection && (
                  <Dropdown<ColumnPresetOption>
                    id={`content-export-${item.id}-preset`}
                    className={styles.presetDropdown}
                    size="sm"
                    titleText=""
                    hideLabel
                    label="Column format"
                    aria-label={`${item.label} column format`}
                    // A preset is meaningless until the section is included in the export.
                    disabled={!checkedIds.has(item.id)}
                    items={COLUMN_PRESETS}
                    itemToString={(preset) => preset?.label ?? ''}
                    selectedItem={
                      COLUMN_PRESETS.find(
                        (preset) =>
                          preset.id === columnPresets.value[presetSection]
                      ) ?? undefined
                    }
                    onChange={({ selectedItem }) => {
                      if (selectedItem) {
                        columnPresets.onChange(presetSection, selectedItem.id);
                      }
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Spatial data options — shown when the Spatial Data checkbox is checked. */}
      {checkedIds.has('spatial_data') && (
        <div className={styles.spatialOptions}>
          <div className={styles.imageOptionsTitle}>Spatial data options</div>
          <p className={styles.imageOptionsHint}>
            Check the attributes to export for each layer — geometry is always
            included, so a layer with no attributes checked exports geometry
            only. Each selected layer is exported in every selected format —
            e.g. Slots + Units as both Shapefile and KML. Coordinate system:
            WGS84 (EPSG:4326).
          </p>

          <div className={styles.spatialBasis}>
            {isPackageMode ? (
              /* Package mode: editable date + time picker. */
              <div className={styles.dateTimePicker}>
                <DatePicker
                  id="spatial-as-of-date"
                  labelText="As-of date"
                  size="sm"
                  value={extractDate(spatialConfig.as_of_date)}
                  onChange={(dates: Date[]) => {
                    const [date] = dates;
                    if (date) {
                      const datePart = date.toISOString().slice(0, 10);
                      const timePart = extractTime(spatialConfig.as_of_date);
                      onSpatialConfigChange({
                        ...spatialConfig,
                        as_of_date: `${datePart}T${timePart}`,
                      });
                    }
                  }}
                />
                <TimePicker
                  id="spatial-as-of-time"
                  labelText="Time"
                  size="sm"
                  value={extractTime(spatialConfig.as_of_date)}
                  pattern="([01]?[0-9]|2[0-3]):[0-5][0-9]"
                  placeholder="HH:MM"
                  maxLength={5}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const time = e.target.value;
                    if (/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(time)) {
                      const datePart =
                        extractDate(spatialConfig.as_of_date) ??
                        new Date().toISOString().slice(0, 10);
                      onSpatialConfigChange({
                        ...spatialConfig,
                        as_of_date: `${datePart}T${time}`,
                      });
                    }
                  }}
                />
              </div>
            ) : null}
          </div>

          {/* Per-layer picker (INS-13268): the shared SpatialLayerPicker (one copy, also rendered by
              the Map Query / Areas / Type Curves dialog). Each layer is a parent checkbox expanding to
              an attribute grid; the header is select-all / clear-all, ticking an attribute re-includes
              an unchecked layer, and a checked layer with no attributes is a geometry-only export.
              `attributes ?? {}` passes the per-layer selection; defaultSpatialConfig seeds every layer
              with its full attribute set, so a layer entry is never absent in practice. */}
          <SpatialLayerPicker
            layers={SPATIAL_EXPORT_LAYERS}
            layerIds={spatialConfig.layers}
            attributesByLayer={spatialConfig.attributes ?? {}}
            onChange={({ layerIds, attributesByLayer }) =>
              onSpatialConfigChange({
                ...spatialConfig,
                layers: layerIds,
                attributes: attributesByLayer,
              })
            }
          />

          <div
            role="group"
            aria-label="Formats"
            className={styles.spatialFormats}
          >
            <strong className={styles.spatialFormatsLabel}>Formats</strong>
            <div className={styles.spatialFormatOptions}>
              {['shapefile', 'kml', 'kmz'].map((fmt) => (
                <Checkbox
                  key={fmt}
                  id={`spatial-format-${fmt}`}
                  labelText={fmt.toUpperCase()}
                  checked={spatialConfig.formats.includes(fmt)}
                  onChange={(_e, { checked }) => {
                    const next = checked
                      ? [...spatialConfig.formats, fmt]
                      : spatialConfig.formats.filter((f) => f !== fmt);
                    onSpatialConfigChange({ ...spatialConfig, formats: next });
                  }}
                />
              ))}
            </div>
          </div>

          {spatialExportInvalid && !isExporting && (
            <p className={styles.spatialValidation} role="alert">
              {SPATIAL_EXPORT_VALIDATION_HINT}
            </p>
          )}
        </div>
      )}

      {/* Image Options — one form governs every captured image (the BE ZIPs whatever PNGs it
          receives). Shown when an image item is checked. All six aspect ratios; the generic
          chart-element toggles; PNG/JPEG. */}
      {showImageOptions && (
        <div className={styles.imageOptions}>
          <div className={styles.imageOptionsTitle}>Image options</div>
          <p className={styles.imageOptionsHint}>
            Applied to every image in this export.
          </p>
          <ExportImageOptionsFields
            value={imageOptions}
            onChange={onImageOptionsChange}
            elementDescriptors={GENERIC_CHART_ELEMENTS}
            supportedFormats={IMAGE_EXPORT_FORMATS}
            aspectOptions={ALL_ASPECT_RATIO_OPTIONS}
          />
        </div>
      )}

      {error && (
        <InlineNotification
          className={styles.error}
          kind="error"
          lowContrast
          hideCloseButton
          title="Export failed"
          subtitle={error}
        />
      )}
    </Modal>
  );
}
